import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  ShieldCheck,
  Download,
  Terminal,
  ExternalLink,
  CheckCircle2,
  Copy,
  FileCode,
  KeyRound,
  Globe,
} from 'lucide-react';

const ENVIRONMENTS = [
  {
    value: 'homologacion',
    label: 'Homologación',
    shortLabel: 'Homo',
    certField: 'cert_homo',
    keyField: 'key_homo',
    aliasKey: 'afip_certificate_alias_homo',
  },
  {
    value: 'produccion',
    label: 'Producción',
    shortLabel: 'Prod',
    certField: 'cert_prod',
    keyField: 'key_prod',
    aliasKey: 'afip_certificate_alias_prod',
  },
];

const normalizeCuit = (value) => String(value || '').replace(/\D/g, '').slice(0, 11);

const formatCuit = (value) => {
  const digits = normalizeCuit(value);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

const sanitizeAlias = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .slice(0, 40);

const slugify = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const downloadTextFile = (filename, content) => {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const buildOpenSslConfig = ({ taxName, alias, cuit }) => `# Config OpenSSL para ARCA/AFIP
[ req ]
default_bits = 2048
prompt = no
default_md = sha256
distinguished_name = dn

[ dn ]
C = AR
O = ${taxName}
CN = ${alias}
serialNumber = CUIT ${formatCuit(cuit)}`;

const buildWindowsScript = ({ configFilename, keyFilename, csrFilename, crtFilename }) => `@echo off
setlocal
cd /d "%~dp0"

set "CFG=${configFilename}"

if not exist "%CFG%" (
  for %%F in (openssl-afip-*.cnf) do (
    if /I not "%%~nxF"=="${configFilename}" (
      set "CFG=%%~nxF"
      goto :cfg_found
    )
  )
)

:cfg_found

openssl version >nul 2>&1
if errorlevel 1 (
  echo ERROR: OpenSSL no esta disponible en el PATH.
  echo Instala OpenSSL y vuelve a ejecutar este archivo.
  pause
  exit /b 1
)

if not exist "%CFG%" (
  echo ERROR: No se encontro ningun archivo .cnf para OpenSSL en esta carpeta.
  echo Esperado: ${configFilename}
  echo Descarga el .cnf desde Gastify y colocalo junto al script.
  pause
  exit /b 1
)

if /I not "%CFG%"=="${configFilename}" (
  echo AVISO: No estaba ${configFilename}. Se usara %CFG%.
)

echo [1/2] Generando llave privada...
openssl genrsa -out ${keyFilename} 2048
if errorlevel 1 (
  echo ERROR: No se pudo generar ${keyFilename}
  pause
  exit /b 1
)

echo [2/2] Generando CSR...
openssl req -new -key ${keyFilename} -config "%CFG%" -out ${csrFilename}
if errorlevel 1 (
  echo ERROR: No se pudo generar ${csrFilename}
  pause
  exit /b 1
)

echo.
echo Listo. Archivos generados:
echo - ${keyFilename}
echo - ${csrFilename}
echo.
echo Siguiente paso:
echo 1. Subi ${csrFilename} en ARCA/AFIP
echo 2. Descarga el certificado y guardalo como ${crtFilename}
pause`;

const buildShellScript = ({ configFilename, keyFilename, csrFilename, crtFilename }) => `#!/usr/bin/env bash
set -euo pipefail

cd "$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"

CFG="${configFilename}"

if ! command -v openssl >/dev/null 2>&1; then
  echo "ERROR: OpenSSL no esta disponible en el PATH."
  exit 1
fi

if [[ ! -f "$CFG" ]]; then
  FALLBACK="$(find . -maxdepth 1 -type f -name 'openssl-afip-*.cnf' | head -n 1 | sed 's#^\\./##')"
  if [[ -n "$FALLBACK" ]]; then
    CFG="$FALLBACK"
    echo "AVISO: No estaba ${configFilename}. Se usara $CFG."
  else
    echo "ERROR: No se encontro ningun archivo .cnf para OpenSSL en esta carpeta."
    echo "Esperado: ${configFilename}"
    exit 1
  fi
fi

echo "[1/2] Generando llave privada..."
openssl genrsa -out ${keyFilename} 2048

echo "[2/2] Generando CSR..."
openssl req -new -key ${keyFilename} -config "$CFG" -out ${csrFilename}

echo
echo "Listo. Archivos generados:"
echo "- ${keyFilename}"
echo "- ${csrFilename}"
echo
echo "Siguiente paso:"
echo "1. Subi ${csrFilename} en ARCA/AFIP"
echo "2. Descarga el certificado y guardalo como ${crtFilename}"`;

const buildValidationWindowsScript = ({ keyFilename, crtFilename }) => `@echo off
setlocal
cd /d "%~dp0"

if not exist "${crtFilename}" (
  echo ERROR: Falta ${crtFilename}
  pause
  exit /b 1
)

if not exist "${keyFilename}" (
  echo ERROR: Falta ${keyFilename}
  pause
  exit /b 1
)

for /f "delims=" %%A in ('openssl x509 -noout -modulus -in ${crtFilename} ^| openssl md5') do set CRT=%%A
for /f "delims=" %%B in ('openssl rsa -noout -modulus -in ${keyFilename} ^| openssl md5') do set KEY=%%B

echo MD5 CRT: %CRT%
echo MD5 KEY: %KEY%
echo.
echo Si ambos valores coinciden, el par KEY/CRT es correcto.
pause`;

const buildValidationShellScript = ({ keyFilename, crtFilename }) => `#!/usr/bin/env bash
set -euo pipefail

cd "$(cd "$(dirname "\${BASH_SOURCE[0]}")" && pwd)"

if [[ ! -f "${crtFilename}" ]]; then
  echo "ERROR: Falta ${crtFilename}"
  exit 1
fi

if [[ ! -f "${keyFilename}" ]]; then
  echo "ERROR: Falta ${keyFilename}"
  exit 1
fi

CRT="$(openssl x509 -noout -modulus -in ${crtFilename} | openssl md5)"
KEY="$(openssl rsa -noout -modulus -in ${keyFilename} | openssl md5)"

echo "MD5 CRT: $CRT"
echo "MD5 KEY: $KEY"
echo
echo "Si ambos valores coinciden, el par KEY/CRT es correcto."`;

const buildChecklist = ({
  environmentLabel,
  taxName,
  alias,
  cuit,
  keyFilename,
  csrFilename,
  crtFilename,
  certField,
  keyField,
}) => `# Checklist certificado ${environmentLabel} ARCA/AFIP

## Datos del emisor
- Razon social: ${taxName}
- CUIT: ${formatCuit(cuit)}
- Alias: ${alias}

## Archivos a generar localmente
- KEY: ${keyFilename}
- CSR: ${csrFilename}
- CRT esperado: ${crtFilename}

## Paso 1 - Generar CSR
1. Descarga el archivo .cnf y el script para tu sistema operativo.
2. Ejecuta el script en una carpeta con OpenSSL disponible.
3. Confirma que obtuviste ${keyFilename} y ${csrFilename}.

## Paso 2 - Portal ARCA/AFIP
1. Ingresa con Clave Fiscal.
2. Abre "Administracion de Certificados Digitales".
3. Crea o usa el alias ${alias}.
4. Sube ${csrFilename}.
5. Descarga el certificado y guardalo como ${crtFilename}.

## Paso 3 - Delegar permiso
1. Ve a "Administrador de Relaciones de Clave Fiscal".
2. Nueva Relacion.
3. AFIP / Web Services / Facturacion Electronica.
4. Selecciona el alias ${alias}.
5. Confirma.

## Paso 4 - Cargar en Gastify
- CRT destino: ${certField}
- KEY destino: ${keyField}

## Paso 5 - Validacion tecnica
Ejecuta el script 02_validar_* y verifica que el MD5 del modulus coincida para CRT y KEY.`;

async function copyToClipboard(text) {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

function StepCard({ icon: Icon, step, title, badge, children }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[var(--surface-2)] text-[var(--text-default)]">
            <Icon size={18} />
          </div>
          <div>
            <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Paso {step}</div>
            <div className="mt-1 text-[12px] font-black uppercase tracking-wide text-[var(--color-accent-dark)]">{title}</div>
          </div>
        </div>
        <span className="rounded-xl border border-[color:var(--border)] bg-[var(--surface-2)] px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">
          {badge}
        </span>
      </div>
      <div className="mt-3 text-[11px] font-bold leading-5 text-[var(--text-muted)]">{children}</div>
    </div>
  );
}

function CommandCard({ title, command, onCopy, copied }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">{title}</div>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[var(--surface)] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] transition-all hover:text-[#059669]"
        >
          <Copy size={14} />
          {copied ? 'Copiado' : 'Copiar'}
        </button>
      </div>
      <pre className="mt-3 overflow-x-auto rounded-md border border-[color:var(--border)] bg-slate-950 px-4 py-3 text-[11px] font-bold leading-5 text-slate-200">
        {command}
      </pre>
    </div>
  );
}

export default function AfipCertificateWizard({
  billingEntity,
  organizationName = '',
  fiscalMeta = {},
  onUpdateFiscalMeta = () => {},
}) {
  const defaultEnvironment = billingEntity?.afip_mode === 'produccion' ? 'produccion' : 'homologacion';
  const [environment, setEnvironment] = useState(defaultEnvironment);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    setEnvironment(defaultEnvironment);
  }, [defaultEnvironment, billingEntity?.id]);

  const environmentConfig = useMemo(
    () => ENVIRONMENTS.find((item) => item.value === environment) || ENVIRONMENTS[0],
    [environment],
  );

  const taxName = String(billingEntity?.tax_name || organizationName || '').trim();
  const cuit = normalizeCuit(billingEntity?.cuit);
  const rawAlias = String(fiscalMeta?.[environmentConfig.aliasKey] || '');
  const suggestedAlias = useMemo(() => {
    const baseName = slugify(taxName) || 'impulza';
    const suffix = environment === 'produccion' ? 'prod' : 'homo';
    return sanitizeAlias(`${baseName}-${suffix}`);
  }, [environment, taxName]);

  const alias = sanitizeAlias(rawAlias) || suggestedAlias;
  const fileBase = slugify(alias || `afip-${environment}`) || `afip-${environment}`;
  const configFilename = `openssl-afip-${environment}.cnf`;
  const keyFilename = `${fileBase}.key`;
  const csrFilename = `${fileBase}.csr`;
  const crtFilename = `${fileBase}.crt`;
  const windowsScriptFilename = `01_generar_${fileBase}.bat`;
  const shellScriptFilename = `01_generar_${fileBase}.sh`;
  const validateWindowsFilename = `02_validar_${fileBase}.bat`;
  const validateShellFilename = `02_validar_${fileBase}.sh`;
  const isReady = taxName.length > 2 && cuit.length === 11;
  const windowsVerifyCommand = 'openssl version';
  const windowsRunCommand = `.${'\\'}${windowsScriptFilename}`;
  const shellVerifyCommand = 'openssl version';
  const shellRunCommand = `chmod +x ./${shellScriptFilename}\n./${shellScriptFilename}`;

  const openSslConfig = useMemo(
    () => buildOpenSslConfig({ taxName: taxName || 'Mi Empresa', alias, cuit: cuit || '30000000000' }),
    [alias, cuit, taxName],
  );

  const windowsScript = useMemo(
    () => buildWindowsScript({ configFilename, keyFilename, csrFilename, crtFilename }),
    [configFilename, crtFilename, csrFilename, keyFilename],
  );

  const shellScript = useMemo(
    () => buildShellScript({ configFilename, keyFilename, csrFilename, crtFilename }),
    [configFilename, crtFilename, csrFilename, keyFilename],
  );

  const validationWindowsScript = useMemo(
    () => buildValidationWindowsScript({ keyFilename, crtFilename }),
    [crtFilename, keyFilename],
  );

  const validationShellScript = useMemo(
    () => buildValidationShellScript({ keyFilename, crtFilename }),
    [crtFilename, keyFilename],
  );

  const checklist = useMemo(
    () =>
      buildChecklist({
        environmentLabel: environmentConfig.label,
        taxName: taxName || 'Mi Empresa',
        alias,
        cuit: cuit || '30000000000',
        keyFilename,
        csrFilename,
        crtFilename,
        certField: environmentConfig.certField,
        keyField: environmentConfig.keyField,
      }),
    [alias, cuit, crtFilename, csrFilename, environmentConfig, keyFilename, taxName],
  );

  const handleCopy = async (text, id) => {
    try {
      await copyToClipboard(text);
      setCopiedId(id);
      toast.success('Copiado al portapapeles.');
      window.setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1800);
    } catch (error) {
      console.error(error);
      toast.error('No se pudo copiar.');
    }
  };

  const handleDownload = (filename, content) => {
    if (!isReady) {
      toast.error('Completa razón social y CUIT del emisor antes de generar archivos.');
      return;
    }
    downloadTextFile(filename, content);
  };

  return (
    <div className="mt-4 rounded-md border border-[color:var(--border)] bg-[#F8FBFF] p-4 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[var(--color-accent-dark)]">
            <ShieldCheck size={14} className="text-[#059669]" />
            Asistente de certificados ARCA/AFIP
          </div>
          <div className="mt-1 text-[10px] font-bold leading-5 text-[var(--text-muted)]">
            Genera el `.cnf`, los scripts para CSR/KEY y la guía para cargar el CRT correcto en este emisor.
          </div>
        </div>

        <div className="rounded-md border border-blue-100 bg-[var(--surface)] px-3 py-2 text-[9px] font-bold uppercase tracking-widest text-[var(--text-muted)]">
          Destino actual: {environmentConfig.certField} + {environmentConfig.keyField}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Ambiente del certificado</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {ENVIRONMENTS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setEnvironment(item.value)}
                className={`rounded-xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                  environment === item.value
                    ? 'border-[#059669] bg-[#059669] text-white shadow-sm'
                    : 'border-[color:var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:text-[#059669]'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)] mb-1">Alias sugerido</div>
              <input
                value={rawAlias}
                onChange={(event) => onUpdateFiscalMeta(environmentConfig.aliasKey, sanitizeAlias(event.target.value))}
                className="w-full rounded-xl border border-[color:var(--border)] bg-[var(--surface)] px-3 py-2 text-[12px] font-bold text-[var(--text-default)]"
                placeholder={suggestedAlias}
              />
              <div className="mt-2 text-[9px] font-bold text-[var(--text-muted)]">
                Si lo dejas vacío usamos <span className="text-[var(--text-muted)]">{suggestedAlias}</span>.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
                <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">CUIT</div>
                <div className="mt-2 text-[12px] font-black text-[var(--text-default)]">{formatCuit(cuit) || 'Pendiente'}</div>
              </div>
              <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
                <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">Alias final</div>
                <div className="mt-2 break-all text-[12px] font-black text-[var(--text-default)]">{alias || 'Pendiente'}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
              <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">Config</div>
              <div className="mt-2 break-all text-[11px] font-black text-[var(--text-default)]">{configFilename}</div>
            </div>
            <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
              <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">KEY</div>
              <div className="mt-2 break-all text-[11px] font-black text-[var(--text-default)]">{keyFilename}</div>
            </div>
            <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
              <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">CSR</div>
              <div className="mt-2 break-all text-[11px] font-black text-[var(--text-default)]">{csrFilename}</div>
            </div>
            <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
              <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">CRT</div>
              <div className="mt-2 break-all text-[11px] font-black text-[var(--text-default)]">{crtFilename}</div>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Validación mínima</div>
          <div className="mt-3 space-y-3">
            <div className={`rounded-md border p-3 ${isReady ? 'border-[color:var(--color-accent)] bg-[var(--color-accent-soft)]' : 'border-amber-200 bg-amber-50'}`}>
              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[var(--color-accent-dark)]">
                <CheckCircle2 size={14} className={isReady ? 'text-[var(--color-accent-dark)]' : 'text-amber-600'} />
                {isReady ? 'Listo para generar archivos' : 'Falta completar el emisor'}
              </div>
              <div className="mt-2 text-[10px] font-bold leading-5 text-[var(--text-muted)]">
                {isReady
                  ? 'Con razón social, CUIT y alias ya podemos generar CSR y scripts.'
                  : 'Necesitas razón social y un CUIT válido de 11 dígitos en el emisor.'}
              </div>
            </div>

            <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-3">
              <div className="text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">Destino en Gastify</div>
              <div className="mt-2 text-[10px] font-bold leading-5 text-[var(--text-muted)]">
                Cuando vuelvas con los archivos:
                <br />
                CRT → <span className="text-[var(--text-default)]">{environmentConfig.certField}</span>
                <br />
                KEY → <span className="text-[var(--text-default)]">{environmentConfig.keyField}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Archivos descargables</div>
              <div className="mt-1 text-[10px] font-bold text-[var(--text-muted)]">Descarga todo lo necesario para generar el pedido localmente.</div>
            </div>
            <div className="rounded-xl border border-[color:var(--border)] bg-[var(--surface-2)] px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-[var(--text-muted)]">
              OpenSSL local
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <button
              type="button"
              onClick={() => handleDownload(configFilename, openSslConfig)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Config</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{configFilename}</div>
              </div>
              <Download size={16} className="text-[#059669]" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(windowsScriptFilename, windowsScript)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Windows</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{windowsScriptFilename}</div>
              </div>
              <Terminal size={16} className="text-[#059669]" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(shellScriptFilename, shellScript)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Linux / macOS</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{shellScriptFilename}</div>
              </div>
              <Terminal size={16} className="text-[var(--color-accent-dark)]" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(`checklist-${fileBase}.md`, checklist)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Checklist</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{`checklist-${fileBase}.md`}</div>
              </div>
              <FileCode size={16} className="text-[var(--text-default)]" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(validateWindowsFilename, validationWindowsScript)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Validación Windows</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{validateWindowsFilename}</div>
              </div>
              <KeyRound size={16} className="text-[var(--text-default)]" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(validateShellFilename, validationShellScript)}
              className="flex items-center justify-between rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] px-4 py-3 text-left transition-all hover:border-[#059669] hover:bg-[#EFF6FF]"
            >
              <div>
                <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Validación Linux / macOS</div>
                <div className="mt-1 text-[11px] font-black text-[var(--text-default)]">{validateShellFilename}</div>
              </div>
              <KeyRound size={16} className="text-[var(--color-accent-dark)]" />
            </button>
          </div>
        </div>

        <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Vista previa del .cnf</div>
              <div className="mt-1 text-[10px] font-bold text-[var(--text-muted)]">Así queda el pedido que va a firmar OpenSSL.</div>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(openSslConfig, 'config')}
              className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[var(--surface)] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[var(--text-muted)] transition-all hover:text-[#059669]"
            >
              <Copy size={14} />
              {copiedId === 'config' ? 'Copiado' : 'Copiar'}
            </button>
          </div>

          <pre className="mt-4 overflow-x-auto rounded-md border border-[color:var(--border)] bg-slate-950 px-4 py-3 text-[11px] font-bold leading-5 text-slate-200">
            {openSslConfig}
          </pre>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-[color:var(--border)] bg-[var(--surface)] p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Cómo se ejecuta</div>
            <div className="mt-1 text-[10px] font-bold leading-5 text-[var(--text-muted)]">
              El archivo <span className="text-[var(--text-default)]">{configFilename}</span> no se ejecuta solo: lo lee OpenSSL cuando corres el script.
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1 text-[8px] font-black uppercase tracking-widest text-amber-700">
            Paso a paso
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
          <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Windows</div>
            <ol className="mt-3 list-decimal space-y-2 pl-4 text-[11px] font-bold leading-5 text-[var(--text-muted)]">
              <li>Descarga <span className="text-[var(--text-default)]">{configFilename}</span> y <span className="text-[var(--text-default)]">{windowsScriptFilename}</span>.</li>
              <li>Guarda ambos archivos en la misma carpeta y procura que sean del mismo ambiente.</li>
              <li>Abre PowerShell dentro de esa carpeta.</li>
              <li>Verifica OpenSSL.</li>
              <li>Ejecuta el script.</li>
              <li>Te tienen que aparecer <span className="text-[var(--text-default)]">{keyFilename}</span> y <span className="text-[var(--text-default)]">{csrFilename}</span>.</li>
            </ol>

            <div className="mt-4 space-y-3">
              <CommandCard
                title="Verificar OpenSSL"
                command={windowsVerifyCommand}
                onCopy={() => handleCopy(windowsVerifyCommand, 'windows-verify')}
                copied={copiedId === 'windows-verify'}
              />
              <CommandCard
                title="Ejecutar script"
                command={windowsRunCommand}
                onCopy={() => handleCopy(windowsRunCommand, 'windows-run')}
                copied={copiedId === 'windows-run'}
              />
            </div>
          </div>

          <div className="rounded-md border border-[color:var(--border)] bg-[var(--surface-2)] p-4">
            <div className="text-[9px] font-black uppercase tracking-widest text-[var(--text-muted)]">Linux / macOS</div>
            <ol className="mt-3 list-decimal space-y-2 pl-4 text-[11px] font-bold leading-5 text-[var(--text-muted)]">
              <li>Descarga <span className="text-[var(--text-default)]">{configFilename}</span> y <span className="text-[var(--text-default)]">{shellScriptFilename}</span>.</li>
              <li>Guarda ambos archivos en la misma carpeta y procura que sean del mismo ambiente.</li>
              <li>Abre Terminal dentro de esa carpeta.</li>
              <li>Verifica OpenSSL.</li>
              <li>Dale permiso al script y ejecútalo.</li>
              <li>Te tienen que aparecer <span className="text-[var(--text-default)]">{keyFilename}</span> y <span className="text-[var(--text-default)]">{csrFilename}</span>.</li>
            </ol>

            <div className="mt-4 space-y-3">
              <CommandCard
                title="Verificar OpenSSL"
                command={shellVerifyCommand}
                onCopy={() => handleCopy(shellVerifyCommand, 'shell-verify')}
                copied={copiedId === 'shell-verify'}
              />
              <CommandCard
                title="Dar permiso y ejecutar"
                command={shellRunCommand}
                onCopy={() => handleCopy(shellRunCommand, 'shell-run')}
                copied={copiedId === 'shell-run'}
              />
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-md border border-blue-100 bg-[#F8FBFF] px-4 py-3 text-[10px] font-bold leading-5 text-[var(--text-muted)]">
          Resumen rápido:
          <br />
          1. El <b>.cnf</b> define los datos del certificado.
          <br />
          2. El <b>script</b> corre OpenSSL usando ese `.cnf`.
          <br />
          3. El resultado es la <b>KEY</b> y el <b>CSR</b>.
          <br />
          4. El <b>CSR</b> se sube a ARCA/AFIP para bajar después el <b>CRT</b>.
        </div>

        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[10px] font-bold leading-5 text-amber-800">
          Importante: no mezcles descargas de <b>homologación</b> con descargas de <b>producción</b> en la misma prueba.
          Si cambias de ambiente, vuelve a descargar tanto el <b>.cnf</b> como el <b>script</b>.
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <StepCard icon={Globe} step="1" title="Generar KEY y CSR" badge="Local">
          Descarga <span className="text-[var(--text-default)]">{configFilename}</span> y uno de los scripts. Al ejecutarlo se crean{' '}
          <span className="text-[var(--text-default)]">{keyFilename}</span> y <span className="text-[var(--text-default)]">{csrFilename}</span>.
        </StepCard>

        <StepCard icon={ExternalLink} step="2" title="Subir el CSR a ARCA/AFIP" badge={environmentConfig.shortLabel}>
          En &quot;Administración de Certificados Digitales&quot; crea o reutiliza el alias{' '}
          <span className="text-[var(--text-default)]">{alias}</span>, sube <span className="text-[var(--text-default)]">{csrFilename}</span> y
          descarga el CRT firmado.
        </StepCard>

        <StepCard icon={ShieldCheck} step="3" title="Delegar Facturación Electrónica" badge="Clave Fiscal">
          En &quot;Administrador de Relaciones de Clave Fiscal&quot; crea la relación AFIP → Web Services → Facturación
          Electrónica usando el alias <span className="text-[var(--text-default)]">{alias}</span>.
        </StepCard>

        <StepCard icon={KeyRound} step="4" title="Subir archivos en Gastify" badge="ERP">
          Renombra el certificado descargado a <span className="text-[var(--text-default)]">{crtFilename}</span>, valida el par con el
          script 02 y luego carga CRT en <span className="text-[var(--text-default)]">{environmentConfig.certField}</span> y KEY en{' '}
          <span className="text-[var(--text-default)]">{environmentConfig.keyField}</span>.
        </StepCard>
      </div>

      <div className="mt-4 rounded-md border border-blue-100 bg-[var(--surface)] px-4 py-3 text-[9px] font-bold leading-5 text-[var(--text-muted)]">
        Este asistente deja el alias en <b>borrador</b> dentro del emisor actual. Para conservarlo junto al resto de la
        configuración, guarda el emisor después de terminar.
      </div>
    </div>
  );
}
